import { Link } from 'react-router-dom'
import t1 from '../assets/t1.jpg'
import t2 from '../assets/t2.jpg'
import truckload from '../assets/truckload.avif'
import './Transport.css'

const SERVICES = [
  {
    icon: '🚢',
    title: 'Cargo Shipping',
    desc: 'End-to-end ocean freight solutions covering FCL and LCL shipments, port coordination, and multimodal connectivity across African and global trade lanes.',
  },
  {
    icon: '🚂',
    title: 'Rail Freight',
    desc: 'Reliable rail logistics for bulk and containerised cargo, delivering high-capacity movement along regional corridors with predictable transit times.',
  },
  {
    icon: '🚛',
    title: 'Truckload Services',
    desc: 'Full-truckload and less-than-truckload road transport with GPS-tracked fleets, experienced drivers, and last-mile delivery.',
  },
  {
    icon: '📋',
    title: 'Customs & Compliance',
    desc: 'Complete import-export facilitation — documents, tariff classification, duty optimisation, and real-time customs liaison so your cargo never stalls at the border.',
  },
  {
    icon: '📡',
    title: 'Supply Chain Visibility',
    desc: 'Live tracking dashboards, automated status alerts, and data-driven analytics give you full transparency and proactive issue resolution at every step.',
  },
  {
    icon: '💰',
    title: 'Cost Efficiency',
    desc: 'Route optimisation, load consolidation, and strategic carrier partnerships translate directly into measurable cost savings and improved margins for your business.',
  },
]

const STATS = [
  { value: '500+', label: 'Shipments delivered' },
  { value: '12+', label: 'Border corridors covered' },
  { value: '98%', label: 'On-time performance' },
  { value: '24/7', label: 'Operations support' },
]

function TransportPage({ lang }) {
  const isFr = lang === 'fr'

  return (
    <div className="tp-page">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="tp-hero">
        <div className="tp-hero-overlay" />
        <img src={truckload} alt="Truckload transport fleet" className="tp-hero-bg" />
        <div className="tp-hero-content">
          <span className="tp-hero-eyebrow">
            {isFr ? 'Transport & Logistique' : 'Transport & Logistics'}
          </span>
          <h1 className="tp-hero-title">
            {isFr
              ? 'Élevez votre chaîne d\'approvisionnement avec une logistique intelligente'
              : 'Elevate Your Supply Chain with Intelligent Logistics'}
          </h1>
          <p className="tp-hero-sub">
            {isFr
              ? 'Nous nous spécialisons dans des opérations de transit haute performance et une logistique de corridor qui intègre parfaitement le fret maritime, ferroviaire et routier.'
              : 'We specialize in high-performance transit operations and corridor logistics that seamlessly integrate cargo shipping, rail freight, and truckload services.'}
          </p>
          <div className="tp-hero-actions">
            <Link to="/contact" className="tp-btn primary">
              {isFr ? 'Démarrer une expédition' : 'Start Shipping'}
            </Link>
            <Link to="/contact" className="tp-btn outline">
              {isFr ? 'Dédouanement & Transit' : 'Customs & Transit'}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats bar ────────────────────────────────────────── */}
      <section className="tp-stats">
        {STATS.map(s => (
          <div key={s.label} className="tp-stat">
            <span className="tp-stat-value">{s.value}</span>
            <span className="tp-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* ── Intro text ───────────────────────────────────────── */}
      <section className="tp-intro">
        <div className="tp-intro-inner">
          <div className="tp-intro-text">
            <h2 className="tp-section-title">
              {isFr ? 'Votre partenaire logistique de confiance' : 'Your Trusted Logistics Partner'}
            </h2>
            <p>
              {isFr
                ? 'Notre équipe d\'experts assure une facilitation complète des importations-exportations et de la conformité, en traitant les douanes et la documentation avec précision et rapidité. Appuyés par des outils de pointe en matière de visibilité et d\'efficacité de la chaîne d\'approvisionnement, vous bénéficiez d\'une transparence totale, d\'une résolution proactive des problèmes et d\'économies mesurables — transformant la logistique mondiale complexe en un avantage concurrentiel fluide pour votre entreprise.'
                : 'Our expert team provides complete import-export facilitation and compliance, navigating customs and documentation with precision and speed. Backed by cutting-edge supply chain visibility and efficiency tools, you enjoy full transparency, proactive issue resolution, and measurable cost savings — turning complex global logistics into a smooth, competitive advantage for your business.'}
            </p>
          </div>
          <div className="tp-intro-images">
            <img src={t1} alt="Transport operations" className="tp-intro-img tp-intro-img-top" />
            <img src={t2} alt="Logistics fleet" className="tp-intro-img tp-intro-img-bottom" />
          </div>
        </div>
      </section>

      {/* ── Services grid ────────────────────────────────────── */}
      <section className="tp-services">
        <div className="tp-services-inner">
          <h2 className="tp-section-title tp-center">
            {isFr ? 'Nos Services' : 'Our Services'}
          </h2>
          <p className="tp-section-sub tp-center">
            {isFr
              ? 'Des solutions logistiques complètes du point d\'origine à la destination finale.'
              : 'End-to-end logistics solutions from origin to final destination.'}
          </p>
          <div className="tp-services-grid">
            {SERVICES.map(s => (
              <div key={s.title} className="tp-service-card">
                <span className="tp-service-icon">{s.icon}</span>
                <h3 className="tp-service-title">{s.title}</h3>
                <p className="tp-service-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature strip ────────────────────────────────────── */}
      <section className="tp-feature">
        <div className="tp-feature-img-wrap">
          <img src={t2} alt="Logistics fleet on the road" className="tp-feature-img" />
        </div>
        <div className="tp-feature-text">
          <h2>
            {isFr
              ? 'De la frontière à la destination — sans friction'
              : 'From Border to Destination — Friction-Free'}
          </h2>
          <ul className="tp-feature-list">
            {[
              isFr ? 'Opérations de transit et logistique de corridor' : 'Transit operations and corridor logistics',
              isFr ? 'Facilitation import-export et conformité réglementaire' : 'Import-export facilitation and compliance',
              isFr ? 'Visibilité et outils d\'efficacité de la chaîne d\'approvisionnement' : 'Supply chain visibility and efficiency tools',
              isFr ? 'Résolution proactive des incidents' : 'Proactive issue resolution',
              isFr ? 'Économies mesurables sur vos coûts logistiques' : 'Measurable cost savings on logistics spend',
            ].map(item => (
              <li key={item} className="tp-feature-item">
                <span className="tp-feature-check">✓</span>
                {item}
              </li>
            ))}
          </ul>
          <Link to="/contact" className="tp-btn primary" style={{ marginTop: 28, display: 'inline-block' }}>
            {isFr ? 'Parlez à notre équipe' : 'Talk to Our Team'}
          </Link>
        </div>
      </section>

      {/* ── CTA banner ───────────────────────────────────────── */}
      <section className="tp-cta-banner">
        <div className="tp-cta-inner">
          <h2>{isFr ? 'Prêt à optimiser votre logistique ?' : 'Ready to Optimise Your Logistics?'}</h2>
          <p>{isFr ? 'Contactez-nous dès aujourd\'hui pour un devis personnalisé.' : 'Contact us today for a personalised quote and logistics assessment.'}</p>
          <div className="tp-hero-actions">
            <Link to="/contact" className="tp-btn primary">
              {isFr ? 'Obtenir un devis' : 'Get a Quote'}
            </Link>
            <Link to="/contact" className="tp-btn outline tp-outline-dark">
              {isFr ? 'En savoir plus' : 'Learn More'}
            </Link>
          </div>
        </div>
      </section>

    </div>
  )
}

export default TransportPage
